import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GraduationCap, PlayCircle, ChevronDown, ChevronRight, CheckCircle2, Circle, ArrowRight, Lightbulb, X, Pencil, RotateCcw, Save } from 'lucide-react';
import TranslatedText from '@/components/TranslatedText';
import { toast } from 'sonner';

const instruments = [
  { key: '', label: 'Tous' },
  { key: 'guitar', label: 'Guitare' },
  { key: 'piano', label: 'Piano' },
  { key: 'voice', label: 'Chant' },
  { key: 'drums', label: 'Batterie' },
  { key: 'bass', label: 'Basse' },
];
const levels = [
  { key: '', label: 'Tous niveaux' },
  { key: 'beginner', label: 'Débutant' },
  { key: 'intermediate', label: 'Intermédiaire' },
  { key: 'advanced', label: 'Avancé' },
  { key: 'pro', label: 'Pro' },
];

export default function Courses() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [courses, setCourses] = useState([]);
  const [instrument, setInstrument] = useState('');
  const [level, setLevel] = useState('');
  const [selected, setSelected] = useState(null); // course object
  const [openLessonIdx, setOpenLessonIdx] = useState(0);
  const [completedIndices, setCompletedIndices] = useState([]);

  useEffect(() => {
    (async () => {
      const params = {};
      if (instrument) params.instrument = instrument;
      if (level) params.level = level;
      const { data } = await api.get('/courses', { params });
      setCourses(data);
    })();
  }, [instrument, level]);

  const openCourse = async (c) => {
    setSelected(c);
    try {
      const { data } = await api.get(`/progress/course/${c.id}`);
      setCompletedIndices(data.completed_indices || []);
      // Auto-open the first uncompleted lesson
      const firstUncompleted = c.lessons.findIndex((_, i) => !data.completed_indices.includes(i));
      setOpenLessonIdx(firstUncompleted >= 0 ? firstUncompleted : 0);
    } catch {
      setCompletedIndices([]);
      setOpenLessonIdx(0);
    }
  };

  const closeCourse = () => { setSelected(null); setCompletedIndices([]); };

  const markComplete = async (lessonIdx) => {
    try {
      await api.post('/progress', {
        course_id: selected.id,
        lesson_index: lessonIdx,
        status: 'completed',
        minutes_practiced: 10,
      });
      const newCompleted = [...new Set([...completedIndices, lessonIdx])].sort((a, b) => a - b);
      setCompletedIndices(newCompleted);
      toast.success('Leçon terminée ! Continue comme ça 🎉');
      // Auto-advance to next lesson
      if (lessonIdx < selected.lessons.length - 1) {
        setOpenLessonIdx(lessonIdx + 1);
      }
    } catch { toast.error('Impossible d\'enregistrer'); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10">
        <div className="mb-8 rise">
          <div className="overline text-orange-400 mb-2">Parcours</div>
          <h1 className="font-display text-5xl tracking-tight">Cours structurés</h1>
          <p className="text-stone-400 mt-2 max-w-2xl">Des programmes clairs pour progresser du débutant au professionnel, par instrument. Clique sur chaque leçon pour voir son contenu détaillé, la vidéo, et marque-la terminée pour suivre ta progression.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {instruments.map(i => (
            <button
              key={i.key}
              data-testid={`course-inst-${i.key || 'all'}`}
              onClick={() => setInstrument(i.key)}
              className={`px-4 py-2 rounded-full text-sm border ${instrument === i.key ? 'bg-orange-700 border-orange-700 text-white' : 'bg-transparent border-stone-700 text-stone-300 hover:border-stone-500'}`}
            >{i.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-10">
          {levels.map(l => (
            <button
              key={l.key}
              data-testid={`course-level-${l.key || 'all'}`}
              onClick={() => setLevel(l.key)}
              className={`px-4 py-2 rounded-full text-sm border ${level === l.key ? 'bg-orange-700 border-orange-700 text-white' : 'bg-transparent border-stone-700 text-stone-300 hover:border-stone-500'}`}
            >{l.label}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {courses.map((c, i) => (
            <CourseCard key={c.id} c={c} i={i} onOpen={openCourse} />
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={closeCourse}>
          <div
            className="glass rounded-2xl max-w-4xl w-full my-8 p-8"
            onClick={e => e.stopPropagation()}
            data-testid="course-modal"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="overline text-orange-400">{selected.instrument} · {selected.level}</div>
                <h2 className="font-display text-4xl mt-1">
                  <TranslatedText text={selected.title} cacheKey={`course-title-${selected.id}`} />
                </h2>
                <p className="text-stone-400 mt-2">
                  <TranslatedText text={selected.description} cacheKey={`course-desc-${selected.id}`} />
                </p>
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <div className="text-stone-300">
                    <span className="font-display text-2xl text-orange-300">{completedIndices.length}</span>
                    <span className="text-stone-500"> / {selected.lessons.length} leçons</span>
                  </div>
                  <div className="flex-1 h-1.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-600 transition-all" style={{ width: `${(completedIndices.length / selected.lessons.length) * 100}%` }} />
                  </div>
                </div>
              </div>
              <button onClick={closeCourse} data-testid="close-course-modal" className="p-2 text-stone-400 hover:text-stone-100 rounded-full hover:bg-stone-800/60">
                <X size={22} />
              </button>
            </div>

            <div className="space-y-2">
              {selected.lessons.map((l, idx) => {
                const isDone = completedIndices.includes(idx);
                const isOpen = openLessonIdx === idx;
                return (
                  <div key={idx} className={`border rounded-xl transition-colors ${isOpen ? 'border-orange-800/60 bg-orange-950/10' : 'border-stone-800 bg-stone-950/40'}`}>
                    <button
                      data-testid={`lesson-toggle-${idx}`}
                      onClick={() => setOpenLessonIdx(isOpen ? -1 : idx)}
                      className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-stone-900/40"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isDone
                          ? <CheckCircle2 size={20} className="text-emerald-400 shrink-0" />
                          : <Circle size={20} className="text-stone-600 shrink-0" />
                        }
                        <div className="min-w-0">
                          <div className="font-display text-xl truncate">
                            {idx + 1}. <TranslatedText text={l.title} cacheKey={`lesson-title-${selected.id}-${idx}`} />
                          </div>
                          <div className="text-xs text-stone-500">{l.duration}</div>
                        </div>
                      </div>
                      {isOpen ? <ChevronDown size={18} className="text-stone-500 shrink-0" /> : <ChevronRight size={18} className="text-stone-500 shrink-0" />}
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-5 space-y-4 border-t border-stone-800/60 pt-4" data-testid={`lesson-content-${idx}`}>
                        <LessonVideoPicker
                          lesson={l}
                          courseId={selected.id}
                          lessonIndex={idx}
                          isAdmin={isAdmin}
                          onOverrideSaved={async () => {
                            // Reload courses to pick up the new override
                            const { data } = await api.get('/courses');
                            setCourses(data);
                            const fresh = data.find(c => c.id === selected.id);
                            if (fresh) setSelected(fresh);
                          }}
                        />
                        <div className="prose prose-invert prose-stone max-w-none">
                          <div className="text-stone-300 whitespace-pre-wrap leading-relaxed text-sm">
                            {formatMarkdown(l.content)}
                          </div>
                        </div>
                        {l.tips && l.tips.length > 0 && (
                          <div className="rounded-lg border border-orange-900/40 bg-orange-950/20 p-4">
                            <div className="flex items-center gap-2 text-orange-300 font-medium text-sm mb-2">
                              <Lightbulb size={14} /> Astuces
                            </div>
                            <ul className="space-y-1 text-sm text-stone-300">
                              {l.tips.map((tip, ti) => (
                                <li key={ti} className="flex gap-2">
                                  <span className="text-orange-500">→</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap pt-2">
                          {isDone ? (
                            <div className="btn-ghost text-emerald-400 inline-flex items-center gap-2 text-sm cursor-default">
                              <CheckCircle2 size={16} /> Leçon terminée
                            </div>
                          ) : (
                            <button
                              data-testid={`mark-lesson-${idx}`}
                              onClick={() => markComplete(idx)}
                              className="btn-primary text-sm inline-flex items-center gap-2"
                            >
                              <CheckCircle2 size={16} /> Marquer comme terminée
                            </button>
                          )}
                          {idx < selected.lessons.length - 1 && (
                            <button
                              data-testid={`next-lesson-${idx}`}
                              onClick={() => setOpenLessonIdx(idx + 1)}
                              className="btn-ghost text-sm inline-flex items-center gap-2"
                            >
                              Leçon suivante <ArrowRight size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function CourseCard({ c, i, onOpen }) {
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/progress/course/${c.id}`);
        setProgress(data);
      } catch { /* not signed in? */ }
    })();
  }, [c.id]);

  const pct = progress && c.lessons?.length
    ? Math.round((progress.count / c.lessons.length) * 100)
    : 0;

  return (
    <div className="card-stone overflow-hidden rise" style={{ animationDelay: `${i*60}ms` }}>
      <div className="grid grid-cols-3">
        <div className="col-span-1 bg-stone-800 aspect-square relative">
          {c.cover_url && <img src={c.cover_url} alt={c.title} className="w-full h-full object-cover" />}
          {progress && progress.count > 0 && (
            <div className="absolute bottom-1 left-1 right-1 h-1 bg-black/50 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
        <div className="col-span-2 p-5">
          <div className="overline text-stone-500">{c.instrument} · {c.level}</div>
          <div className="font-display text-2xl mt-1">
            <TranslatedText text={c.title} cacheKey={`course-title-${c.id}`} />
          </div>
          <div className="text-stone-400 text-sm mt-2 leading-relaxed line-clamp-2">
            <TranslatedText text={c.description} cacheKey={`course-desc-${c.id}`} />
          </div>
          <div className="mt-3 text-xs text-stone-500 flex items-center gap-2">
            <GraduationCap size={14} /> {c.lessons?.length || 0} leçons
            {progress && progress.count > 0 && (
              <span className="text-orange-300">· {progress.count}/{c.lessons.length} terminées</span>
            )}
          </div>
          <button
            data-testid={`open-course-${i}`}
            onClick={() => onOpen(c)}
            className="mt-4 btn-primary text-sm inline-flex items-center gap-2"
          >
            <PlayCircle size={16} />
            {progress && progress.count > 0 ? 'Reprendre' : 'Commencer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple markdown-like formatter: bold **text**, and preserve line breaks.
function formatMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Split by **bold**
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div key={i} className={line.trim() === '' ? 'h-3' : ''}>
        {parts.map((p, pi) => {
          if (p.startsWith('**') && p.endsWith('**')) {
            return <strong key={pi} className="text-orange-200 font-semibold">{p.slice(2, -2)}</strong>;
          }
          return <span key={pi}>{p}</span>;
        })}
      </div>
    );
  });
}

function LessonVideoPicker({ lesson, courseId, lessonIndex, isAdmin = false, onOverrideSaved }) {
  const videos = lesson.video_urls && lesson.video_urls.length > 0
    ? lesson.video_urls
    : (lesson.video_url ? [{ url: lesson.video_url, label: 'Tutoriel', lang: 'en' }] : []);
  const initialIdx = Math.max(0, videos.findIndex(v => v.lang === 'fr'));
  const [videoIdx, setVideoIdx] = useState(initialIdx);
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState('');
  const [draftLabel, setDraftLabel] = useState('Tutoriel FR');
  const [saving, setSaving] = useState(false);
  // Build a YouTube search URL from the lesson title in French
  const searchQuery = encodeURIComponent(`tutoriel ${lesson.title} français débutant`);
  const searchUrl = `https://www.youtube.com/results?search_query=${searchQuery}`;

  const startEdit = () => {
    const cur = videos[0] || {};
    setDraftUrl(cur.url || '');
    setDraftLabel(cur.label || 'Tutoriel FR');
    setEditing(true);
  };
  const saveOverride = async () => {
    if (!draftUrl.trim()) { toast.error('URL manquante'); return; }
    let url = draftUrl.trim();
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
    if (m) url = `https://www.youtube.com/embed/${m[1]}`;
    setSaving(true);
    try {
      await api.put(`/admin/courses/${courseId}/lessons/${lessonIndex}/videos`, {
        video_urls: [{ url, label: draftLabel || 'Tutoriel', lang: 'fr' }],
      });
      toast.success('Vidéo mise à jour');
      setEditing(false);
      if (onOverrideSaved) await onOverrideSaved();
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    } finally { setSaving(false); }
  };
  const resetOverride = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Restaurer la vidéo par défaut ?')) return;
    setSaving(true);
    try {
      await api.delete(`/admin/courses/${courseId}/lessons/${lessonIndex}/videos`);
      toast.success('Retour au seed');
      if (onOverrideSaved) await onOverrideSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const AdminEditor = () => (
    <div className="rounded-lg border border-orange-800/60 bg-stone-900/80 p-3 mb-3 space-y-2" data-testid={`admin-inline-editor-${lessonIndex}`}>
      <div className="text-[10px] uppercase tracking-wider text-orange-400 font-bold">Éditer la vidéo · Admin</div>
      <input
        value={draftUrl}
        onChange={e => setDraftUrl(e.target.value)}
        placeholder="URL YouTube ou ID vidéo"
        data-testid={`admin-inline-url-${lessonIndex}`}
        className="w-full px-3 py-2 rounded-lg bg-stone-950 border border-stone-800 text-stone-100 text-xs font-mono"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={draftLabel}
          onChange={e => setDraftLabel(e.target.value)}
          placeholder="Label"
          className="flex-1 min-w-[120px] px-3 py-1.5 rounded-lg bg-stone-950 border border-stone-800 text-stone-100 text-xs"
        />
        <button onClick={saveOverride} disabled={saving} data-testid={`admin-inline-save-${lessonIndex}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-700 hover:bg-orange-600 text-white text-xs disabled:opacity-50">
          <Save size={11} /> Enregistrer
        </button>
        <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-full bg-stone-800 hover:bg-stone-700 text-stone-300 text-xs">Annuler</button>
      </div>
    </div>
  );

  const AdminActions = () => isAdmin && !editing && (
    <div className="mb-2 flex gap-2 flex-wrap">
      <button onClick={startEdit} data-testid={`admin-inline-open-${lessonIndex}`} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-orange-950 hover:bg-orange-900 text-orange-300 border border-orange-800/60">
        <Pencil size={11} /> Éditer vidéo
      </button>
      {videos.length > 0 && (
        <button onClick={resetOverride} disabled={saving} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-stone-900 hover:bg-stone-800 text-stone-400 border border-stone-800 disabled:opacity-50">
          <RotateCcw size={11} /> Restaurer seed
        </button>
      )}
    </div>
  );

  // No embedded video — show a curated search-only fallback so learners always
  // have an easy way to find matching French content on YouTube.
  if (videos.length === 0) {
    return (
      <div>
        {editing && <AdminEditor />}
        <AdminActions />
        <a
          href={searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="youtube-fr-search-fallback"
          className="group block rounded-lg border border-dashed border-stone-700 bg-stone-900/60 hover:border-orange-700 hover:bg-stone-900 transition-colors p-5 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-orange-400 group-hover:text-orange-300">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.5 6.19c-.24-1.04-1-1.9-2-2.18C19.7 3.5 12 3.5 12 3.5s-7.7 0-9.5.5c-1 .28-1.76 1.14-2 2.19C0 8 0 12 0 12s0 4 .5 5.81c.24 1.04 1 1.9 2 2.18 1.8.5 9.5.5 9.5.5s7.7 0 9.5-.5c1-.28 1.76-1.14 2-2.19.5-1.81.5-5.81.5-5.81s0-4-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/></svg>
            <span className="text-sm font-semibold">Trouver un tutoriel vidéo en français</span>
          </div>
          <p className="mt-1.5 text-xs text-stone-500 group-hover:text-stone-400">
            Recherche YouTube pour « {lesson.title} »
          </p>
        </a>
      </div>
    );
  }

  const active = videos[videoIdx];
  return (
    <div>
      {editing && <AdminEditor />}
      <AdminActions />
      {videos.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {videos.map((v, i) => (
            <button
              key={i}
              onClick={() => setVideoIdx(i)}
              data-testid={`video-tab-${i}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                i === videoIdx
                  ? 'bg-orange-700 border-orange-700 text-white'
                  : 'bg-stone-900 border-stone-800 text-stone-400 hover:border-stone-700'
              }`}
            >
              <span className="uppercase text-[9px] tracking-wider font-bold opacity-70">{v.lang}</span>
              <span>{v.label}</span>
            </button>
          ))}
        </div>
      )}
      <div className="aspect-video rounded-lg overflow-hidden border border-stone-800 bg-stone-900">
        <iframe
          key={active.url}
          src={active.url}
          title={active.label || lesson.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      {videos.length === 1 && (
        <p className="mt-1.5 text-[11px] text-stone-500 italic">
          Vidéo de référence — pour un cours pas-à-pas, utilise le lien de recherche ci-dessous.
        </p>
      )}
      <a
        href={searchUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="youtube-fr-search"
        className="mt-2 inline-flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.19c-.24-1.04-1-1.9-2-2.18C19.7 3.5 12 3.5 12 3.5s-7.7 0-9.5.5c-1 .28-1.76 1.14-2 2.19C0 8 0 12 0 12s0 4 .5 5.81c.24 1.04 1 1.9 2 2.18 1.8.5 9.5.5 9.5.5s7.7 0 9.5-.5c1-.28 1.76-1.14 2-2.19.5-1.81.5-5.81.5-5.81s0-4-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/></svg>
        Chercher des tutoriels français sur YouTube →
      </a>
    </div>
  );
}

